import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, ArrowRight, MapPin, Plane } from "lucide-react";

const TransferDestinations = () => {
  const { data: destinations, isLoading } = useActiveTransferDestinations();

  return (
    <Layout>
      <Helmet>
        <title>Transfer Destinations | Smart Punta Cana Transfer</title>
        <meta name="description" content="Explore our transfer destinations across the Dominican Republic. Premium service, competitive prices, and 24/7 availability." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-accent" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-secondary/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto text-white">
            <Badge className="mb-6 bg-secondary/90 text-secondary-foreground border-0 text-sm px-6 py-2 rounded-full">
              <MapPin className="h-4 w-4 mr-1 inline" /> All Destinations
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[0.9]">
              Transfer <span className="text-secondary">Destinations</span>
            </h1>
            <p className="text-xl text-white/80 font-light">
              Premium transportation to the best destinations in the Dominican Republic
            </p>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-20 bg-background">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent" />
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin-slow w-10 h-10 border-4 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : destinations && destinations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destinations.map((destination) => (
                <Card
                  key={destination.id}
                  className="overflow-hidden hover-lift border-0 shadow-md group"
                >
                  {destination.hero_image && (
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={destination.hero_image}
                        alt={destination.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <h2 className="text-xl font-bold text-white drop-shadow-lg">{destination.name}</h2>
                      </div>
                    </div>
                  )}
                  <CardContent className="p-6">
                    {!destination.hero_image && (
                      <h2 className="text-xl font-bold mb-2">{destination.name}</h2>
                    )}
                    {destination.description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2 text-sm">
                        {destination.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {destination.estimated_time && (
                        <Badge className="bg-primary/10 text-primary border-primary/20 rounded-full text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {destination.estimated_time}
                        </Badge>
                      )}
                      {destination.from_airport_price && (
                        <Badge className="bg-secondary/10 text-secondary border-secondary/20 rounded-full text-xs">
                          <DollarSign className="h-3 w-3 mr-1" />
                          From ${destination.from_airport_price}
                        </Badge>
                      )}
                    </div>

                    {destination.highlights && destination.highlights.length > 0 && (
                      <ul className="space-y-1 mb-4">
                        {destination.highlights.slice(0, 3).map((highlight, index) => (
                          <li key={index} className="text-xs text-muted-foreground flex items-start">
                            <span className="text-secondary mr-2 font-bold">✓</span>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    )}

                    <Link to={`/traslados/${destination.slug}`}>
                      <Button className="w-full rounded-full bg-primary hover:bg-primary/90 group">
                        View Details
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No destinations available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary to-accent text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Plane className="h-10 w-10 mx-auto mb-4 text-secondary animate-float" />
            <h2 className="text-3xl md:text-4xl font-black mb-4">Can't Find Your Destination?</h2>
            <p className="text-white/80 mb-8 font-light">
              Contact us and we'll help you arrange a transfer to any destination in the Dominican Republic
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-8 font-bold shadow-secondary">
                Contact Us Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TransferDestinations;
